/* ============================================================
   exit-intent.js — Exit-intent popup
   Triggers a <dialog> when user shows exit intent
   (desktop: mouse leaves top of page; mobile: fast scroll up).
   Respeita settings da seção: data-delay (segundos) e
   data-days (frequência em dias via localStorage).
   ============================================================ */
(function () {
  'use strict';

  const SESSION_KEY = 'joaninha:exit-intent-shown';
  const LAST_SHOWN_KEY = 'joaninha:exit-intent-last';
  const DIALOG_SELECTOR = '[data-exit-intent]';
  const CLOSE_SELECTOR = '[data-exit-close]';
  const SCROLL_THRESHOLD = 0.4;
  const SCROLL_UP_SPEED_THRESHOLD = 1.2;
  let returnFocus;

  function getDialog() {
    return document.querySelector(DIALOG_SELECTOR);
  }

  function getDays() {
    const dialog = getDialog();
    return parseInt((dialog && dialog.dataset.days) || '7', 10);
  }

  function getArmDelayMs() {
    const dialog = getDialog();
    return (parseInt((dialog && dialog.dataset.delay) || '0', 10) || 0) * 1000;
  }

  function shownRecently() {
    try {
      const last = parseInt(localStorage.getItem(LAST_SHOWN_KEY) || '0', 10);
      if (!last) return false;
      return (Date.now() - last) < getDays() * 86400000;
    } catch (e) { return false; }
  }

  function hasShown() {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === 'true') return true;
    } catch (e) { /* noop */ }
    return shownRecently();
  }

  function markShown() {
    try {
      sessionStorage.setItem(SESSION_KEY, 'true');
      localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
    } catch (e) { /* noop */ }
  }

  function showDialog() {
    if (hasShown()) return;
    const dialog = getDialog();
    if (!dialog) return;
    if (dialog.open) return;
    if (document.querySelector('dialog[open]') || document.querySelector('[data-consent-banner]:not([hidden])')) return;
    returnFocus = document.activeElement;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    markShown();
    document.body.classList.add('exit-intent-open');
    dialog.addEventListener('close', () => {
      document.body.classList.remove('exit-intent-open');
      if (returnFocus && returnFocus.isConnected && returnFocus !== document.body) returnFocus.focus({ preventScroll: true });
    }, { once: true });
  }

  function closeDialog() {
    const dialog = getDialog();
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    document.body.classList.remove('exit-intent-open');
  }

  /* ---- Desktop: mouse leaves through top ---- */
  function onMouseOut(e) {
    if (e.clientY <= 0 && e.relatedTarget === null) {
      showDialog();
      if (hasShown()) document.removeEventListener('mouseout', onMouseOut);
    }
  }

  /* ---- Mobile: fast scroll up after scrolling down ---- */
  let lastScrollY = window.scrollY;
  let lastTime = performance.now();
  let reachedThreshold = false;
  let upwardDistance = 0;
  let upwardStarted = 0;

  function onScroll() {
    const now = performance.now();
    const currentY = window.scrollY;
    const delta = currentY - lastScrollY;
    const deltaTime = now - lastTime;
    const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (pageHeight > 0 && currentY / pageHeight >= SCROLL_THRESHOLD) reachedThreshold = true;
    if (delta >= 0 || deltaTime > 180) { upwardDistance = 0; upwardStarted = now; }
    if (delta < 0 && deltaTime <= 180) {
      upwardDistance += -delta;
      const duration = Math.max(16, now - upwardStarted);
      if (reachedThreshold && upwardDistance >= 80 && upwardDistance / duration > SCROLL_UP_SPEED_THRESHOLD) {
        showDialog();
        if (hasShown()) window.removeEventListener('scroll', onScroll);
      }
    }
    lastScrollY = currentY;
    lastTime = now;
  }

  /* ---- Events ---- */
  function initialize() {
    if (hasShown()) return;
    setTimeout(() => {
      if (hasShown()) return;
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (isTouch) {
        return; // Touch scrolling is navigation, not exit intent.
      } else {
        document.addEventListener('mouseout', onMouseOut);
      }
    }, getArmDelayMs());
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  document.addEventListener('click', (e) => {
    if (e.target.closest(CLOSE_SELECTOR) || e.target.matches(DIALOG_SELECTOR)) {
      closeDialog();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDialog();
  });

  /* ---- Copy coupon code (delegated; works for any [data-copy-btn]) ---- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-btn]');
    if (!btn) return;
    const holder = btn.closest('[data-copy-code]');
    const code = holder ? holder.getAttribute('data-copy-code') : '';
    if (!code) return;
    const status = getDialog()?.querySelector('[data-copy-status]');
    btn.disabled = true;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        let copied;
        try { copied = document.execCommand('copy'); } finally { ta.remove(); btn.focus({ preventScroll: true }); }
        if (!copied) throw new Error('Copy failed');
      }
      if (status) status.textContent = holder.dataset.copySuccess;
    } catch (err) {
      if (status) status.textContent = holder.dataset.copyError;
    }
    finally { btn.disabled = false; }
  });

  window.ExitIntentAPI = { showDialog, closeDialog };
})();
